import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { defineMessages } from 'react-intl';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSLTConfig } from '../../../../slt/config/SLTConfigProvider';
import { QueryKey, rawUrlHash } from '../../../../utils/api';
import { getLinksUrls } from '../utils/getLinksUrls';
import { links as linksToRetrieve } from './links';
import type { FooterLink, LinkGroupProps, ResolvedFooterLink } from '../types';

export function useAboutLinks(): UseQueryResult<LinkGroupProps> {
    const { urlMap } = useSLTConfig();
    const allUrlsFound = useMemo(
        () => linksToRetrieve.every((link) => rawUrlHash(link.url) in urlMap),
        [urlMap],
    );
    return useQuery({
        queryKey: [QueryKey.ABOUT_LINKS],
        initialData: {
            title: messages.aboutSectionTitle,
            containerDataTestId: 'footer-about-us-links-container',
            headerDataTestId: 'footer-about-us-links-header',
            links: footerLinks.map((fl) => {
                const linkToRetrieve = linksToRetrieve.find((ltr) => ltr.id === fl.id);
                return {
                    ...fl,
                    url:
                        fl.url ??
                        (linkToRetrieve?.url
                            ? (urlMap[rawUrlHash(linkToRetrieve?.url)] ?? '')
                            : ''),
                    isPwaLink: false,
                };
            }),
        },
        queryFn: async () => {
            const resolvedLinksMap = await getLinksUrls(urlMap, linksToRetrieve);

            const responseLinks: ResolvedFooterLink[] = footerLinks.map((fl) => ({
                ...fl,
                url: fl.url ?? resolvedLinksMap.get(fl.id) ?? '',
                isPwaLink: false,
            }));

            return {
                title: messages.aboutSectionTitle,
                links: responseLinks,
                containerDataTestId: 'footer-about-us-links-container',
                headerDataTestId: 'footer-about-us-links-header',
            };
        },
        retryOnMount: !allUrlsFound,
    });
}

const messages = defineMessages({
    aboutSectionTitle: {
        id: 'footer.about_us.link_title',
        defaultMessage: 'ABOUT SUR LA TABLE',
    },
    aboutUsTitle: {
        id: 'footer.about_us.about_us_title',
        defaultMessage: 'Go to About Us',
    },
    aboutUsLabel: {
        id: 'footer.about_us.about_us_label',
        defaultMessage: 'ABOUT US',
    },
    careersTitle: {
        id: 'footer.about_us.careers_title',
        defaultMessage: 'CAREERS',
    },
    careersLabel: {
        id: 'footer.about_us.careers_label',
        defaultMessage: 'CAREERS',
    },
    privacyTitle: {
        id: 'footer.about_us.privacy_title',
        defaultMessage: 'PRIVACY',
    },
    privacyLabel: {
        id: 'footer.about_us.privacy_label',
        defaultMessage: 'PRIVACY',
    },
    mediaRelationsTitle: {
        id: 'footer.about_us.media_relations_title',
        defaultMessage: 'MEDIA RELATIONS',
    },
    mediaRelationsLabel: {
        id: 'footer.about_us.media_relations_label',
        defaultMessage: 'MEDIA RELATIONS',
    },
    affiliateProgramsTitle: {
        id: 'footer.about_us.affiliate_programs_title',
        defaultMessage: 'AFFILIATE PROGRAMS',
    },
    affiliateProgramsLabel: {
        id: 'footer.about_us.affiliate_programs_label',
        defaultMessage: 'AFFILIATE PROGRAMS',
    },
    couponPromotionsTitle: {
        id: 'footer.about_us.coupon_promotions_title',
        defaultMessage: 'Coupons, deals and promotions at Sur La Table',
    },
    couponPromotionsLabel: {
        id: 'footer.about_us.coupon_promotions_label',
        defaultMessage: 'COUPONS & DEALS',
    },
    makeMoreTitle: {
        id: 'footer.about_us.make_more_title',
        defaultMessage: 'MAKE MORE',
    },
    makeMoreLabel: {
        id: 'footer.about_us.make_more_label',
        defaultMessage: '#MAKEMORE',
    },
});

const footerLinks: FooterLink[] = [
    {
        id: 1,
        title: messages.aboutUsTitle,
        label: messages.aboutUsLabel,
        dataTestId: 'footer.about_us.about_us',
    },
    {
        id: 2,
        title: messages.careersTitle,
        label: messages.careersLabel,
        dataTestId: 'footer.about_us.careers',
    },
    {
        id: 3,
        title: messages.privacyTitle,
        label: messages.privacyLabel,
        dataTestId: 'footer.about_us.privacy',
    },
    {
        id: 4,
        title: messages.mediaRelationsTitle,
        label: messages.mediaRelationsLabel,
        dataTestId: 'footer.about_us.media_relations',
    },
    {
        id: 5,
        title: messages.affiliateProgramsTitle,
        label: messages.affiliateProgramsLabel,
        dataTestId: 'footer.about_us.affiliate_programs',
    },
    {
        id: 6,
        title: messages.couponPromotionsTitle,
        label: messages.couponPromotionsLabel,
        dataTestId: 'footer.about_us.coupon_promotions',
    },
    {
        id: 7,
        title: messages.makeMoreTitle,
        label: messages.makeMoreLabel,
        dataTestId: 'footer.about_us.make_more',
    },
];
